import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';

const INITIAL_EMPLOYEES = [
  { id: 'EMP-001', name: 'Kwesi Mensah', role: 'Operations Director', department: 'Operations', status: 'Active', location: 'Accra HQ', joinDate: 'Jan 2020', avatar: 'https://i.pravatar.cc/150?img=11' },
  { id: 'EMP-002', name: 'Ama Appiah', role: 'HR Manager', department: 'Human Resources', status: 'Active', location: 'Accra HQ', joinDate: 'Mar 2021', avatar: 'https://i.pravatar.cc/150?img=32' },
  { id: 'EMP-003', name: 'Samuel Boateng', role: 'Logistics Coordinator', department: 'Logistics', status: 'On Leave', location: 'Tema Port', joinDate: 'Aug 2022', avatar: 'https://i.pravatar.cc/150?img=12' },
  { id: 'EMP-004', name: 'Grace Asante', role: 'Recruitment Specialist', department: 'Human Resources', status: 'Active', location: 'Remote', joinDate: 'Feb 2023', avatar: 'https://i.pravatar.cc/150?img=44' },
  { id: 'EMP-005', name: 'David Tetteh', role: 'Immigration Consultant', department: 'Immigration & Legal', status: 'Active', location: 'Accra HQ', joinDate: 'Nov 2021', avatar: 'https://i.pravatar.cc/150?img=53' },
];

const Employees = () => {
  const [employees, setEmployees] = useState(INITIAL_EMPLOYEES);
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({
    name: '', role: '', department: 'Operations', location: '', status: 'Active'
  });

  const handleAddEmployee = (e) => {
    e.preventDefault();
    const newEmp = {
      id: `EMP-00${employees.length + 1}`,
      name: formData.name,
      role: formData.role,
      department: formData.department,
      status: formData.status,
      location: formData.location || 'Accra HQ',
      joinDate: new Date().toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }),
      avatar: `https://i.pravatar.cc/150?img=${Math.floor(Math.random() * 70)}`
    };
    
    setEmployees([newEmp, ...employees]);
    setShowModal(false);
    setFormData({ name: '', role: '', department: 'Operations', location: '', status: 'Active' });
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Employee Database</h1>
          <p className="text-gray-500 text-sm">Manage staff records, onboarding, and organizational charts.</p>
        </div>
        <button 
          onClick={() => setShowModal(true)}
          className="bg-[#11469A] text-white px-4 py-2.5 rounded-lg font-bold text-sm flex items-center gap-2 shadow-sm hover:bg-[#0d3678] transition-all"
        >
          <SafeIcon name="UserPlus" size={18} /> Add Employee
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="card p-4 flex items-center gap-4 border border-gray-100 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-blue-50 text-[#11469A] flex items-center justify-center"><SafeIcon name="Users" size={24} /></div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Total Staff</p>
            <p className="text-xl font-bold text-gray-800">{employees.length}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4 border border-gray-100 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-green-50 text-green-600 flex items-center justify-center"><SafeIcon name="UserCheck" size={24} /></div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Active</p>
            <p className="text-xl font-bold text-gray-800">{employees.filter(e => e.status === 'Active').length}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4 border border-gray-100 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-yellow-50 text-yellow-600 flex items-center justify-center"><SafeIcon name="Coffee" size={24} /></div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">On Leave</p>
            <p className="text-xl font-bold text-gray-800">{employees.filter(e => e.status === 'On Leave').length}</p>
          </div>
        </div>
        <div className="card p-4 flex items-center gap-4 border border-gray-100 shadow-sm">
          <div className="w-12 h-12 rounded-full bg-purple-50 text-purple-600 flex items-center justify-center"><SafeIcon name="UserPlus" size={24} /></div>
          <div>
            <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Onboarding</p>
            <p className="text-xl font-bold text-gray-800">3</p>
          </div>
        </div>
      </div>

      <div className="card border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row gap-4 justify-between items-center bg-gray-50/50">
          <div className="relative w-full sm:w-80">
            <SafeIcon name="Search" size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Search employees..." className="w-full pl-9 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:border-[#11469A] outline-none" />
          </div>
          <button className="flex items-center gap-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 px-4 py-2 rounded-lg hover:bg-gray-50">
            <SafeIcon name="Filter" size={16} /> Filters
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm whitespace-nowrap">
            <thead className="bg-white text-gray-400 font-bold uppercase text-[10px] tracking-widest border-b border-gray-100">
              <tr>
                <th className="px-6 py-4">Employee</th>
                <th className="px-6 py-4">Role & Dept</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Location</th>
                <th className="px-6 py-4">Join Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 bg-white">
              {employees.map(emp => (
                <tr key={emp.id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-3">
                    <div className="flex items-center gap-3">
                      <img src={emp.avatar} className="w-9 h-9 rounded-full object-cover border border-gray-200" alt="" />
                      <div>
                        <div className="font-bold text-gray-800">{emp.name}</div>
                        <div className="text-[10px] text-gray-400 font-medium">{emp.id}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-3">
                    <div className="font-medium text-gray-800 text-xs">{emp.role}</div>
                    <div className="text-[11px] text-gray-500">{emp.department}</div>
                  </td>
                  <td className="px-6 py-3">
                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                      emp.status === 'Active' ? 'bg-green-50 text-green-600 border border-green-100' : 'bg-yellow-50 text-yellow-600 border border-yellow-100'
                    }`}>
                      {emp.status}
                    </span>
                  </td>
                  <td className="px-6 py-3 text-gray-500 text-xs">{emp.location}</td>
                  <td className="px-6 py-3 text-gray-500 text-xs">{emp.joinDate}</td>
                  <td className="px-6 py-3 text-right">
                    <button className="p-1.5 text-gray-400 hover:text-[#11469A] transition-colors rounded hover:bg-blue-50">
                      <SafeIcon name="MoreVertical" size={16} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md overflow-hidden">
            <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-[#f8f9fa]">
              <h3 className="font-bold text-[#11469A]">Add New Employee</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <SafeIcon name="X" size={20} />
              </button>
            </div>
            <form onSubmit={handleAddEmployee} className="p-6 space-y-4">
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Full Name</label>
                <input required type="text" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-[#11469A] outline-none" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} />
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Job Role</label>
                <input required type="text" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-[#11469A] outline-none" value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Department</label>
                  <select className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-[#11469A] outline-none" value={formData.department} onChange={e => setFormData({...formData, department: e.target.value})}>
                    <option>Operations</option>
                    <option>Human Resources</option>
                    <option>Logistics</option>
                    <option>Immigration & Legal</option>
                    <option>Engineering</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Status</label>
                  <select className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-[#11469A] outline-none" value={formData.status} onChange={e => setFormData({...formData, status: e.target.value})}>
                    <option>Active</option>
                    <option>Onboarding</option>
                    <option>On Leave</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">Location</label>
                <input required type="text" placeholder="e.g. Accra HQ" className="w-full px-3 py-2 border border-gray-200 rounded text-sm focus:border-[#11469A] outline-none" value={formData.location} onChange={e => setFormData({...formData, location: e.target.value})} />
              </div>
              <div className="pt-4 flex gap-3">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-2.5 border border-gray-200 text-gray-600 rounded font-bold text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" className="flex-1 py-2.5 bg-[#11469A] text-white rounded font-bold text-sm hover:bg-[#0d3678]">Save Employee</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Employees;